// src/admin/AdminAnalytics.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import {
  FiDollarSign, FiShoppingCart, FiPackage, FiUsers, FiTrendingUp,
  FiCalendar, FiRefreshCw, FiTag, FiBarChart2, FiDownload,
  FiClock, FiUserCheck, FiCreditCard, FiMapPin, FiAward
} from 'react-icons/fi';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar, LineChart, Line,
  ComposedChart
} from 'recharts';
import styles from './AdminAnalytics.module.css';

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState({
    revenue: 0,
    orders: 0,
    customers: 0,
    avgOrderValue: 0,
    conversionRate: 0,
    revenueGrowth: 0,
    orderGrowth: 0,
  });
  const [revenueData, setRevenueData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [customerTrend, setCustomerTrend] = useState([]);
  const [categorySales, setCategorySales] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  const [dayOfWeekData, setDayOfWeekData] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [stateData, setStateData] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);

    const now = new Date();
    const startDate = new Date();
    if (period === 'all') {
      startDate.setFullYear(2000);
    } else {
      startDate.setDate(now.getDate() - parseInt(period));
    }
    const startISO = startDate.toISOString();

    // ─── Orders ────────────────────────────────────────────────
    const { data: orders, error: ordersError } = await supabase
      .from('orders')
      .select('*, order_items(*)')
      .gte('created_at', startISO)
      .order('created_at', { ascending: true });

    if (ordersError) console.error('Orders error:', ordersError);

    const paidOrders = orders?.filter(o => o.payment_status === 'paid') || [];
    const totalRevenue = paidOrders.reduce((sum, o) => sum + o.total_amount, 0);
    const totalOrders = orders?.length || 0;
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // ─── Customers ─────────────────────────────────────────────
    const { count: totalCustomers, error: customersError } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true });

    const conversionRate = totalCustomers > 0 ? (totalOrders / totalCustomers) * 100 : 0;

    // ─── Growth ──────────────────────────────────────────────
    const previousStart = new Date(startDate);
    previousStart.setDate(previousStart.getDate() - parseInt(period));
    const { data: prevOrders } = await supabase
      .from('orders')
      .select('total_amount')
      .eq('payment_status', 'paid')
      .gte('created_at', previousStart.toISOString())
      .lt('created_at', startISO);
    const prevRevenue = prevOrders?.reduce((sum, o) => sum + o.total_amount, 0) || 0;
    const revenueGrowth = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue) * 100 : 0;

    const prevOrdersCount = prevOrders?.length || 0;
    const orderGrowth = prevOrdersCount > 0 ? ((totalOrders - prevOrdersCount) / prevOrdersCount) * 100 : 0;

    setStats({
      revenue: totalRevenue,
      orders: totalOrders,
      customers: totalCustomers || 0,
      avgOrderValue,
      conversionRate,
      revenueGrowth,
      orderGrowth,
    });

    // ─── Revenue by Date ──────────────────────────────────────
    const revenueMap = {};
    orders?.forEach(o => {
      const date = o.created_at.split('T')[0];
      revenueMap[date] = (revenueMap[date] || 0) + o.total_amount;
    });
    const start = new Date(startISO);
    const end = new Date();
    const dateRange = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      dateRange.push(dateStr);
    }
    const revenueData = dateRange.map(date => ({
      date,
      revenue: revenueMap[date] || 0,
      orders: orders?.filter(o => o.created_at.startsWith(date)).length || 0,
    }));
    setRevenueData(revenueData);

    // ─── Order Status ─────────────────────────────────────────
    const statusCount = {};
    orders?.forEach(o => {
      statusCount[o.status] = (statusCount[o.status] || 0) + 1;
    });
    const statusData = Object.keys(statusCount).map(status => ({
      name: status.toUpperCase().replace(/_/g, ' '),
      value: statusCount[status],
    }));
    setOrderStatusData(statusData);

    // ─── Top Products ──────────────────────────────────────────
    const orderIds = orders?.map(o => o.id) || [];
    const { data: items } = await supabase
      .from('order_items')
      .select('product_name, quantity, price')
      .in('order_id', orderIds);

    const productSales = {};
    items?.forEach(item => {
      productSales[item.product_name] = {
        quantity: (productSales[item.product_name]?.quantity || 0) + item.quantity,
        revenue: (productSales[item.product_name]?.revenue || 0) + (item.price * item.quantity),
      };
    });
    const topProducts = Object.keys(productSales)
      .map(name => ({ name, ...productSales[name] }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
    setTopProducts(topProducts);

    // ─── Customer Trend ──────────────────────────────────────
    const { data: customersTrend } = await supabase
      .from('users')
      .select('created_at')
      .gte('created_at', startISO)
      .order('created_at', { ascending: true });
    const customerMap = {};
    customersTrend?.forEach(c => {
      const date = c.created_at.split('T')[0];
      customerMap[date] = (customerMap[date] || 0) + 1;
    });
    const customerData = dateRange.map(date => ({
      date,
      newCustomers: customerMap[date] || 0,
    }));
    setCustomerTrend(customerData);

    // ─── Category Sales ──────────────────────────────────────
    const { data: products } = await supabase
      .from('products')
      .select('id, category, name');

    const orderItemsWithCat = items?.map(item => {
      const product = products?.find(p => p.id === item.product_id);
      return { ...item, category: product?.category || 'Uncategorized' };
    }) || [];

    const categoryMap = {};
    orderItemsWithCat.forEach(item => {
      const cat = item.category || 'Uncategorized';
      categoryMap[cat] = (categoryMap[cat] || 0) + (item.price * item.quantity);
    });
    const categoryData = Object.keys(categoryMap)
      .map(name => ({ name, sales: categoryMap[name] }))
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 6);
    setCategorySales(categoryData);

    // ─── Payment Methods ──────────────────────────────────────
    const paymentMap = {};
    paidOrders.forEach(o => {
      const method = o.payment_method || 'COD';
      paymentMap[method] = (paymentMap[method] || 0) + 1;
    });
    const paymentData = Object.keys(paymentMap).map(method => ({
      name: method === 'RAZORPAY' ? 'Online' : method,
      value: paymentMap[method],
    }));
    setPaymentMethodData(paymentData);

    // ─── Day of Week ──────────────────────────────────────────
    const dayMap = { 'Sun': 0, 'Mon': 0, 'Tue': 0, 'Wed': 0, 'Thu': 0, 'Fri': 0, 'Sat': 0 };
    orders?.forEach(o => {
      const day = new Date(o.created_at).toLocaleString('en', { weekday: 'short' });
      dayMap[day] = (dayMap[day] || 0) + 1;
    });
    const dayData = Object.keys(dayMap).map(day => ({ name: day, orders: dayMap[day] }));
    setDayOfWeekData(dayData);

    // ─── Top Customers ────────────────────────────────────────
    const customerSpend = {};
    orders?.forEach(o => {
      if (o.user_id) {
        customerSpend[o.user_id] = (customerSpend[o.user_id] || 0) + o.total_amount;
      }
    });
    const topCustomers = Object.keys(customerSpend)
      .map(userId => ({ userId, spend: customerSpend[userId] }))
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 5);
    if (topCustomers.length > 0) {
      const userIds = topCustomers.map(c => c.userId);
      const { data: userNames } = await supabase
        .from('users')
        .select('id, name, email')
        .in('id', userIds);
      const nameMap = {};
      userNames?.forEach(u => { nameMap[u.id] = u.name || u.email; });
      topCustomers.forEach(c => { c.name = nameMap[c.userId] || 'Customer'; });
    }
    setTopCustomers(topCustomers);

    // ─── Low Stock Products ──────────────────────────────────
    const { data: lowStock } = await supabase
      .from('products')
      .select('name, stock_quantity')
      .lt('stock_quantity', 10)
      .order('stock_quantity', { ascending: true })
      .limit(10);
    setLowStockProducts(lowStock || []);

    // ─── State Distribution ──────────────────────────────────
    const stateMap = {};
    orders?.forEach(o => {
      if (o.shipping_address) {
        const match = o.shipping_address.match(/(?:[A-Z][a-z]*\s*)+,\s*([A-Za-z\s]+)\s*-\s*\d{6}/);
        if (match && match[1]) {
          const state = match[1].trim();
          stateMap[state] = (stateMap[state] || 0) + 1;
        }
      }
    });
    const stateData = Object.keys(stateMap)
      .map(state => ({ name: state, orders: stateMap[state] }))
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 5);
    setStateData(stateData);

    setLoading(false);
  };

  // ─── Export CSV Report ───────────────────────────────────────
const exportReport = () => {
  setExporting(true);

  let csv = 'Etikoppaka Toys Analytics Report\n';
  csv += `Generated: ${new Date().toLocaleString()}\n`;
  csv += `Period: ${period === 'all' ? 'All Time' : `Last ${period} days`}\n\n`;

  csv += '📊 SUMMARY METRICS\n';
  csv += `Revenue,₹${Math.round(stats.revenue)}\n`;
  csv += `Orders,${stats.orders}\n`;
  csv += `Avg Order Value,₹${Math.round(stats.avgOrderValue)}\n`;
  csv += `Customers,${stats.customers}\n`;
  csv += `Conversion Rate,${stats.conversionRate.toFixed(1)}%\n`;
  csv += `Revenue Growth,${stats.revenueGrowth.toFixed(1)}%\n`;
  csv += `Order Growth,${stats.orderGrowth.toFixed(1)}%\n\n`;

  csv += '📈 REVENUE TREND\n';
  csv += 'Date,Revenue,Orders\n';
  revenueData.forEach(d => {
    csv += `${d.date},₹${Math.round(d.revenue)},${d.orders}\n`;
  });
  csv += '\n';

  csv += '📊 ORDER STATUS\n';
  csv += 'Status,Count\n';
  orderStatusData.forEach(d => {
    csv += `${d.name},${d.value}\n`;
  });
  csv += '\n';

  csv += '🏆 TOP PRODUCTS BY REVENUE\n';
  csv += 'Product,Quantity,Revenue\n';
  topProducts.forEach(d => {
    csv += `${d.name},${d.quantity},₹${Math.round(d.revenue)}\n`;
  });
  csv += '\n';

  csv += '📦 SALES BY CATEGORY\n';
  csv += 'Category,Sales\n';
  categorySales.forEach(d => {
    csv += `${d.name},₹${Math.round(d.sales)}\n`;
  });
  csv += '\n';

  csv += '💳 PAYMENT METHODS\n';
  csv += 'Method,Count\n';
  paymentMethodData.forEach(d => {
    csv += `${d.name},${d.value}\n`;
  });
  csv += '\n';

  csv += '👤 TOP CUSTOMERS\n';
  csv += 'Customer,Total Spend\n';
  topCustomers.forEach(d => {
    csv += `${d.name},₹${Math.round(d.spend)}\n`;
  });
  csv += '\n';

  csv += '📍 TOP STATES\n';
  csv += 'State,Orders\n';
  stateData.forEach(d => {
    csv += `${d.name},${d.orders}\n`;
  });
  csv += '\n';

  if (lowStockProducts.length > 0) {
    csv += '⚠️ LOW STOCK PRODUCTS\n';
    csv += 'Product,Stock\n';
    lowStockProducts.forEach(d => {
      csv += `${d.name},${d.stock_quantity}\n`;
    });
    csv += '\n';
  }

  csv += '📅 ORDERS BY DAY OF WEEK\n';
  csv += 'Day,Orders\n';
  dayOfWeekData.forEach(d => {
    csv += `${d.name},${d.orders}\n`;
  });
  csv += '\n';

  csv += '👥 NEW CUSTOMERS (by date)\n';
  csv += 'Date,New Customers\n';
  customerTrend.slice(-30).forEach(d => {
    csv += `${d.date},${d.newCustomers}\n`;
  });

  // Download CSV (with BOM for UTF-8)
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `analytics_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);

  setExporting(false);
};

  const COLORS = ['#1F5B3A', '#C89B3C', '#9E1B1B', '#3B82F6', '#F59E0B', '#8B5CF6', '#10B981', '#EC4899'];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className={styles.analyticsContainer}>
      {/* Header */}
      <div className={styles.header}>
        <h2>Analytics Dashboard</h2>
        <div className={styles.controls}>
          <FiCalendar className={styles.filterIcon} />
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className={styles.periodSelect}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          <button onClick={fetchAnalytics} className={styles.refreshBtn}>
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#E8F5E9', color: '#1F5B3A' }}>
            <FiDollarSign />
          </div>
          <div className={styles.statInfo}>
            <span>Revenue</span>
            <strong>₹{stats.revenue.toLocaleString()}</strong>
            <span className={stats.revenueGrowth >= 0 ? styles.positive : styles.negative}>
              {stats.revenueGrowth >= 0 ? '↑' : '↓'} {Math.abs(stats.revenueGrowth).toFixed(1)}%
            </span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#E3F2FD', color: '#3B82F6' }}>
            <FiShoppingCart />
          </div>
          <div className={styles.statInfo}>
            <span>Orders</span>
            <strong>{stats.orders}</strong>
            <span className={stats.orderGrowth >= 0 ? styles.positive : styles.negative}>
              {stats.orderGrowth >= 0 ? '↑' : '↓'} {Math.abs(stats.orderGrowth).toFixed(1)}%
            </span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#FFF3E0', color: '#F59E0B' }}>
            <FiPackage />
          </div>
          <div className={styles.statInfo}>
            <span>Avg Order Value</span>
            <strong>₹{stats.avgOrderValue.toFixed(0)}</strong>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#FCE4EC', color: '#EC4899' }}>
            <FiUsers />
          </div>
          <div className={styles.statInfo}>
            <span>Customers</span>
            <strong>{stats.customers}</strong>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#D1FAE5', color: '#10B981' }}>
            <FiUserCheck />
          </div>
          <div className={styles.statInfo}>
            <span>Conversion Rate</span>
            <strong>{stats.conversionRate.toFixed(1)}%</strong>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#FEF3C7', color: '#D97706' }}>
            <FiTrendingUp />
          </div>
          <div className={styles.statInfo}>
            <span>Revenue Growth</span>
            <strong className={stats.revenueGrowth >= 0 ? styles.positive : styles.negative}>
              {stats.revenueGrowth >= 0 ? '+' : ''}{stats.revenueGrowth.toFixed(1)}%
            </strong>
          </div>
        </div>
      </div>

      {/* Revenue & Orders Chart */}
      <div className={styles.fullWidthChart}>
        <div className={styles.chartHeader}>
          <h3><FiTrendingUp /> Revenue & Orders Trend</h3>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={revenueData}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1F5B3A" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#1F5B3A" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis yAxisId="left" tickFormatter={(value) => `₹${value}`} />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip formatter={(value, name) => [name === 'revenue' ? `₹${value}` : value, name === 'revenue' ? 'Revenue' : 'Orders']} />
            <Legend />
            <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#1F5B3A" fill="url(#colorRevenue)" name="Revenue" />
            <Bar yAxisId="right" dataKey="orders" barSize={20} fill="#C89B3C" name="Orders" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Two-Column Charts Row 1 */}
      <div className={styles.chartRow}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Order Status</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={orderStatusData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" label>
                {orderStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Payment Methods</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={paymentMethodData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" label>
                {paymentMethodData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[(index+2) % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two-Column Charts Row 2 */}
      <div className={styles.chartRow}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Top Products by Revenue</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={topProducts} layout="vertical">
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
              <Tooltip formatter={(value) => [`₹${value}`, 'Revenue']} />
              <Bar dataKey="revenue" fill="#9E1B1B" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Sales by Category</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={categorySales}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={(value) => `₹${value}`} />
              <Tooltip formatter={(value) => [`₹${value}`, 'Sales']} />
              <Bar dataKey="sales" fill="#1F5B3A" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two-Column Charts Row 3 */}
      <div className={styles.chartRow}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>New Customers</h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={customerTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="newCustomers" stroke="#3B82F6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Orders by Day of Week</h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dayOfWeekData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="orders" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two-Column: Top Customers & State Distribution */}
      <div className={styles.chartRow}>
        <div className={styles.tableCard}>
          <div className={styles.chartHeader}>
            <h3><FiAward /> Top Customers</h3>
          </div>
          <div className={styles.tableWrapper}>
            <table className={styles.insightTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Total Spend</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.map((c, idx) => (
                  <tr key={c.userId}>
                    <td>{idx+1}</td>
                    <td>{c.name}</td>
                    <td>₹{c.spend.toLocaleString()}</td>
                  </tr>
                ))}
                {topCustomers.length === 0 && (
                  <tr><td colSpan="3">No data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className={styles.tableCard}>
          <div className={styles.chartHeader}>
            <h3><FiMapPin /> Top States</h3>
          </div>
          <div className={styles.tableWrapper}>
            <table className={styles.insightTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>State</th>
                  <th>Orders</th>
                </tr>
              </thead>
              <tbody>
                {stateData.map((s, idx) => (
                  <tr key={s.name}>
                    <td>{idx+1}</td>
                    <td>{s.name}</td>
                    <td>{s.orders}</td>
                  </tr>
                ))}
                {stateData.length === 0 && (
                  <tr><td colSpan="3">No data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Low Stock Alerts */}
      {lowStockProducts.length > 0 && (
        <div className={styles.lowStockCard}>
          <div className={styles.chartHeader}>
            <h3><FiPackage /> Low Stock Alert</h3>
            <span className={styles.lowStockCount}>{lowStockProducts.length} products</span>
          </div>
          <div className={styles.tableWrapper}>
            <table className={styles.insightTable}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Stock</th>
                </tr>
              </thead>
              <tbody>
                {lowStockProducts.map(p => (
                  <tr key={p.name}>
                    <td>{p.name}</td>
                    <td className={styles.lowStockValue}>{p.stock_quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Export Button */}
      <div className={styles.exportSection}>
        <button onClick={exportReport} className={styles.exportBtn} disabled={exporting}>
          <FiDownload /> {exporting ? 'Exporting...' : 'Export Report'}
        </button>
      </div>
    </div>
  );
};

export default AdminAnalytics;